import axios from "axios";
import autobind from "autobind-decorator";
import {config} from "../../config";
import * as API from "../../../libs/types/api/index.d";
import {RootStore} from "../store";

interface APIHandlers {
  auth_token_error: () => void;
}

@autobind
export class APIStore {

  readonly handlers: APIHandlers;
  store: RootStore;

  constructor(store: RootStore, handlers: APIHandlers) {
    this.store = store;
    this.handlers = handlers;
  }

  // API REQUEST HELPERS
  async request(args: RequestArgs): Promise<any> {
    const { path, data, params, headers } = args;
    try {
      const res = await axios({
        baseURL: config.urls.api + "/",
        url: path,
        method: "post",
        headers: headers || {},
        params: params || {},
        data: data || {},
      });
      return res.data;
    }
    catch (e) {
      if (e.response && e.response.data) { // === "error-auth-token"
        if (e.response.data.message === "Invalid Authentication") {
          this.handlers.auth_token_error();
          return { outcome: 1, message: "" };
        }
        else {
          throw e.response.data;
        }
      }
      else {
        throw e;
      }
    }
  }
  async request_public(args: RequestPublicPrivateArgs): Promise<any> {
    const { path, data, params } = args;
    const headers = { "Authorization-Public": config.services.api.public_key };
    return this.request({ path, data, params, headers });
  }
  async request_auth(args: RequestPublicPrivateArgs): Promise<any> {
    const { path, data, params } = args;
    const token = this._get_auth_token();
    if (config.isTest && !token) return;
    const headers = { "Authorization-Dashboard": `${token}` };
    return this.request({ path, data, params, headers });
  }

  // RESELLER
  async reseller_find_public(data: API.ResellerFindPublicRequest): Promise<API.ResellerFindPublicResponse> {
    return this.request_public({
      path: "/resellers/find/dashboard/public",
      data: data,
    });
  }

  // USER
  async user_register_commence(data: API.UserRegisterCommenceRequest): Promise<API.UserRegisterCommenceResponse> {
    return this.request_public({
      path: "/users/register/commence",
      data: data,
    });
  }
  async user_register_complete(data: API.UserRegisterCompleteRequest): Promise<API.UserRegisterCompleteResponse> {
    return this.request_public({
      path: "/users/register/complete",
      data: data,
    });
  }
  async user_login(data: API.UserLoginRequest): Promise<API.UserLoginResponse> {
    return this.request_public({
      path: "/users/login",
      data: data,
    });
  }
  async user_password_reset_init(data: API.UserPasswordResetInitRequest): Promise<API.UserPasswordResetInitResponse> {
    return this.request_public({
      path: "/users/password-reset/init",
      data: data,
    });
  }
  async user_password_reset_change(data: API.UserPasswordResetChangeRequest): Promise<API.UserPasswordResetChangeResponse> {
    return this.request_public({
      path: "/users/password-reset/change",
      data: data,
    });
  }
  async user_token_refresh(): Promise<API.UserTokenRefreshResponse> {
    return this.request_auth({
      path: "/users/token/refresh",
    });
  }
  async user_profile_update(data: API.UserUpdateProfileRequest): Promise<API.UserUpdateProfileResponse> {
    return this.request_auth({
      path: "/users/update/profile",
      data: data,
    });
  }

  // RESTAURANT
  async restaurant_create(data: API.RestaurantCreateRequest): Promise<API.RestaurantCreateResponse> {
    return this.request_auth({
      path: "/restaurants/create",
      data: data,
    });
  }
  async restaurant_update(data: API.RestaurantUpdateRequest): Promise<API.RestaurantUpdateResponse> {
    return this.request_auth({
      path: "/restaurants/update",
      data: data,
    });
  }
  async restaurant_update_subscription(data: API.RestaurantUpdateSubscriptionRequest): Promise<API.RestaurantUpdateSubscriptionResponse> {
    return this.request_auth({
      path: "/restaurants/update/subscription",
      data: data,
    });
  }
  async restaurant_delete(data: API.RestaurantDeleteRequest): Promise<API.RestaurantDeleteResponse> {
    return this.request_auth({
      path: "/restaurants/delete",
      data: data,
    });
  }

  // PROXY
  async proxy_subdomain_check(data: API.ProxySubdomainCheckUpdateRequest): Promise<API.ProxySubdomainCheckUpdateResponse> {
    return this.request_auth({
      path: "/proxy/subdomain/check",
      data: data,
    });
  }
  async proxy_subdomain_update(data: API.ProxySubdomainCheckUpdateRequest): Promise<API.ProxySubdomainCheckUpdateResponse> {
    return this.request_auth({
      path: "/proxy/subdomain/update",
      data: data,
    });
  }
  async proxy_domain_check(data: API.ProxyDomainCheckUpdateRequest): Promise<API.ProxyDomainCheckUpdateResponse> {
    return this.request_auth({
      path: "/proxy/domain/check",
      data: data,
    });
  }
  async proxy_domain_update(data: API.ProxyDomainCheckUpdateRequest): Promise<API.ProxyDomainCheckUpdateResponse> {
    return this.request_auth({
      path: "/proxy/domain/update",
      data: data,
    });
  }
  async proxy_domain_cert_check(data: API.ProxyCertCheckRequest): Promise<API.ProxyCertCheckResponse> {
    return this.request_auth({
      path: "/proxy/cert/check",
      data: data,
    });
  }
  async proxy_domain_cert_issue(data: API.ProxyCertIssueRequest): Promise<API.ProxyCertIssueResponse> {
    return this.request_auth({
      path: "/proxy/cert/issue",
      data: data,
    });
  }

  // STAFF
  async staff_find(data: API.StaffFindRequest): Promise<API.StaffFindResponse> {
    return this.request_auth({
      path: "/dashboard/staff/find",
      data: data,
    });
  }
  async staff_create(data: API.StaffCreateRequest): Promise<API.StaffCreateResponse> {
    return this.request_auth({
      path: "/dashboard/staff/create",
      data: data,
    });
  }
  async staff_update(data: API.StaffUpdateRequest): Promise<API.StaffUpdateResponse> {
    return this.request_auth({
      path: "/dashboard/staff/update",
      data: data,
    });
  }
  async staff_delete(data: API.StaffDeleteRequest): Promise<API.StaffDeleteResponse> {
    return this.request_auth({
      path: "/dashboard/staff/delete",
      data: data,
    });
  }

  // API ACCESS
  async api_find(data: API.APIFindRequest): Promise<API.APIFindResponse> {
    return this.request_auth({
      path: "/dashboard/api/find",
      data: data,
    });
  }
  async api_create(data: API.APICreateRequest): Promise<API.APICreateResponse> {
    return this.request_auth({
      path: "/dashboard/api/create",
      data: data,
    });
  }
  async api_update(data: API.APIUpdateRequest): Promise<API.APIUpdateResponse> {
    return this.request_auth({
      path: "/dashboard/api/update",
      data: data,
    });
  }
  async api_delete(data: API.APIDeleteRequest): Promise<API.APIDeleteResponse> {
    return this.request_auth({
      path: "/dashboard/api/delete",
      data: data,
    });
  }

  // WEBSITE
  async website_find(data: API.DashboardWebsiteFindRequest): Promise<API.DashboardWebsiteFindResponse> {
    return this.request_auth({
      path: "/website/dashboard/find",
      data: data,
    });
  }
  async website_create(data: API.DashboardWebsiteCreateRequest): Promise<API.DashboardWebsiteCreateResponse> {
    return this.request_auth({
      path: "/website/dashboard/create",
      data: data,
    });
  }
  async website_update(data: API.DashboardWebsiteUpdateRequest): Promise<API.DashboardWebsiteUpdateResponse> {
    return this.request_auth({
      path: "/website/dashboard/update",
      data: data,
    });
  }

  // DASHBOARD
  async dashboard_data(): Promise<API.DashboardDataResponse> {
    return this.request_auth({
      path: "/dashboard/data",
    });
  }
  async restaurants(): Promise<API.DashboardRestaurantsResponse> {
    return this.request_auth({
      path: "/dashboard/restaurants",
    });
  }
  async restaurant(data: API.DashboardRestaurantFindRequest): Promise<API.DashboardRestaurantFindResponse> {
    return this.request_auth({
      path: "/dashboard/restaurant",
      data: data,
    });
  }
  async billing_session(data: API.DashboardBillingSessionRequest): Promise<API.DashboardBillingSessionResponse> {
    return this.request_auth({
      path: "/dashboard/billing/session",
      data: data,
    });
  }

  // DASHBOARD - RESTAURANT SPECIFIC
  async reports_basic(data: API.DashboardReportsBasicRequest): Promise<API.DashboardReportsBasicResponse> {
    return this.request_auth({
      path: "/dashboard/reports/basic",
      data: data,
    });
  }

  async orders_board_find(data: API.DashboardOrdersBoardFindRequest): Promise<API.DashboardOrdersBoardFindResponse> {
    return this.request_auth({
      path: "/dashboard/orders/board",
      data: data,
    });
  }
  async orders_find(data: API.DashboardOrdersFindRequest): Promise<API.DashboardOrdersFindResponse> {
    return this.request_auth({
      path: "/dashboard/orders",
      data: data,
    });
  }
  async order_find(data: API.DashboardOrderFindRequest): Promise<API.DashboardOrderFindResponse> {
    return this.request_auth({
      path: "/dashboard/order",
      data: data,
    });
  }
  async order_update_status(data: API.DashboardOrderUpdateStatusRequest): Promise<API.DashboardOrderUpdateStatusResponse> {
    return this.request_auth({
      path: "/dashboard/order/update/status",
      data: data,
    });
  }
  async order_update_ready_time(data: API.DashboardOrderUpdateReadyTimeRequest): Promise<API.DashboardOrderUpdateReadyTimeResponse> {
    return this.request_auth({
      path: "/dashboard/order/update/ready-time",
      data: data,
    });
  }
  async order_book_tookan(data: API.DashboardOrderTookanBookRequest): Promise<API.DashboardOrderTookanBookResponse> {
    return this.request_auth({
      path: "/dashboard/order/tookan/book",
      data: data,
    });
  }
  async order_print(data: APIDashboardPrintOrderRequest): Promise<APIDashboardPrintOrderResponse> {
    return this.request_auth({
      path: "/printing/dashboard/order",
      data: data,
    });
  }
  async order_delete(data: API.DashboardOrderDeleteRequest): Promise<API.DashboardOrderDeleteResponse> {
    return this.request_auth({
      path: "/dashboard/order/delete",
      data: data,
    });
  }
  async order_stripe_refund(data: API.DashboardOrderStripeRefundRequest): Promise<API.DashboardOrderStripeRefundResponse> {
    return this.request_auth({
      path: "/dashboard/order/stripe/refund",
      data: data,
    });
  }
  async order_cardconnect_refund(data: API.DashboardOrderCardConnectRefundRequest): Promise<API.DashboardOrderCardConnectRefundResponse> {
    return this.request_auth({
      path: "/dashboard/order/cardconnect/refund",
      data: data,
    });
  }

  async bookings_find(data: API.DashboardBookingsFindRequest): Promise<API.DashboardBookingsFindResponse> {
    return this.request_auth({
      path: "/dashboard/bookings",
      data: data,
    });
  }
  async booking_find(data: API.DashboardBookingFindRequest): Promise<API.DashboardBookingFindResponse> {
    return this.request_auth({
      path: "/dashboard/booking",
      data: data,
    });
  }
  async booking_update_status(data: API.DashboardBookingUpdateStatusRequest): Promise<API.DashboardBookingUpdateStatusResponse> {
    return this.request_auth({
      path: "/dashboard/booking/update/status",
      data: data,
    });
  }
  async booking_delete(data: API.DashboardBookingDeleteRequest): Promise<API.DashboardBookingDeleteResponse> {
    return this.request_auth({
      path: "/dashboard/booking/delete",
      data: data,
    });
  }

  async customers_find(data: API.DashboardCustomersFindRequest): Promise<API.DashboardCustomersFindResponse> {
    return this.request_auth({
      path: "/dashboard/customers",
      data: data,
    });
  }
  async customer_find(data: API.DashboardCustomerFindRequest): Promise<API.DashboardCustomerFindResponse> {
    return this.request_auth({
      path: "/dashboard/customer",
      data: data,
    });
  }
  async customer_create(data: API.DashboardCustomerCreateRequest): Promise<API.DashboardCustomerCreateResponse> {
    return this.request_auth({
      path: "/dashboard/customer/create",
      data: data,
    });
  }
  async customer_update_email(data: API.DashboardCustomerUpdateEmailRequest): Promise<API.DashboardCustomerUpdateEmailResponse> {
    return this.request_auth({
      path: "/dashboard/customer/update/email",
      data: data,
    });
  }
  async customer_update_password(data: API.DashboardCustomerUpdatePasswordRequest): Promise<API.DashboardCustomerUpdatePasswordResponse> {
    return this.request_auth({
      path: "/dashboard/customer/update/password",
      data: data,
    });
  }
  async customer_age_verification_update(data: API.DashboardCustomerAgeVerificationUpdateRequest): Promise<API.DashboardCustomerAgeVerificationUpdateResponse> {
    return this.request_auth({
      path: "/dashboard/customer/age-verification/update",
      data: data,
    });
  }
  async customer_delete(data: API.DashboardCustomerDeleteRequest): Promise<API.DashboardCustomerDeleteResponse> {
    return this.request_auth({
      path: "/dashboard/customer/delete",
      data: data,
    });
  }

  // HELPERS
  private _get_auth_token(): string {
    return this.store.auth.token || "";
  }

}
